import type { EditOp, ResolvedEdit } from "@desk/core";
import type { SourcePack } from "@desk/core";

export interface TimelineSegment {
  beatId: string;
  /** Inclusive source tick. */
  sourceStartTick: number;
  /** Exclusive source tick. */
  sourceEndTick: number;
  /** Inclusive edited-timeline tick. */
  editStartTick: number;
  /** Exclusive edited-timeline tick. */
  editEndTick: number;
}

export interface EditedTimeline {
  segments: TimelineSegment[];
  durationTicks: number;
}

function beatSourceRange(
  pack: SourcePack,
  beatId: string,
  cut: Extract<EditOp, { kind: "cut" }> | undefined,
): { start: number; end: number } | null {
  const beat = pack.beats.find((b) => b.id === beatId);
  if (!beat) return null;
  if (cut?.mode === "omit") return null;
  if (cut?.mode === "trim" && cut.inTick !== undefined && cut.outTick !== undefined) {
    return { start: cut.inTick, end: cut.outTick };
  }
  return { start: beat.startTick, end: beat.endTick };
}

/** Project keep/omit/trim + reorder into a continuous edited timeline. */
export function buildEditedTimeline(pack: SourcePack, resolved: ResolvedEdit): EditedTimeline {
  const segments: TimelineSegment[] = [];
  let cursor = 0;
  for (const beatId of resolved.beatOrder) {
    const range = beatSourceRange(pack, beatId, resolved.cuts.get(beatId));
    if (!range) continue;
    const len = range.end - range.start;
    if (len <= 0) continue;
    segments.push({
      beatId,
      sourceStartTick: range.start,
      sourceEndTick: range.end,
      editStartTick: cursor,
      editEndTick: cursor + len,
    });
    cursor += len;
  }
  return { segments, durationTicks: cursor };
}

/**
 * Count VISIBLE jump-cuts in the edited timeline (presentation only).
 *
 * A "jump-cut" is a boundary between two consecutive KEPT beats whose source
 * ranges are NOT contiguous: either a beat was cut out between them, or the
 * beats were reordered, so the footage visibly jumps. Two segments are
 * contiguous only when the earlier one's source end tick meets the next one's
 * source start tick. This never feeds `scoreEdit` / `resolveEdit` / pack
 * validation; it is read by the emotion model to make choppy edits read as
 * "edited / not trustworthy" (more Doubt).
 */
export function countJumpCuts(timeline: EditedTimeline): number {
  const segs = timeline.segments;
  let jumps = 0;
  for (let i = 1; i < segs.length; i += 1) {
    if (segs[i - 1]!.sourceEndTick !== segs[i]!.sourceStartTick) jumps += 1;
  }
  return jumps;
}

/** Map edited-timeline tick → source tick (for seeking the master video). */
export function editTickToSourceTick(timeline: EditedTimeline, editTick: number): number {
  if (timeline.segments.length === 0) return 0;
  const clamped = Math.max(0, Math.min(editTick, Math.max(0, timeline.durationTicks - 1)));
  for (const seg of timeline.segments) {
    if (clamped >= seg.editStartTick && clamped < seg.editEndTick) {
      return seg.sourceStartTick + (clamped - seg.editStartTick);
    }
  }
  const last = timeline.segments[timeline.segments.length - 1]!;
  return Math.max(last.sourceStartTick, last.sourceEndTick - 1);
}

export function sourceTickToEditTick(timeline: EditedTimeline, sourceTick: number): number | null {
  for (const seg of timeline.segments) {
    if (sourceTick >= seg.sourceStartTick && sourceTick < seg.sourceEndTick) {
      return seg.editStartTick + (sourceTick - seg.sourceStartTick);
    }
  }
  return null;
}

export function formatTicksAsTime(ticks: number, tickRate: number): string {
  // Clips are always under a minute, so show seconds.tenths (##.#), no m:ss.
  const seconds = ticks / tickRate;
  return seconds.toFixed(1).padStart(4, "0");
}
